import { AfterViewInit, ChangeDetectorRef, Component, effect, forwardRef, inject, input, OnDestroy, OnInit, signal } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormBuilder, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ReactiveFormsModule, ValidationErrors, Validator, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { InputLabel } from '../input-label/input-label.component';
import { CountryDropdown } from '../country-dropdown/country-dropdown';
import { StateProvinceDropdown } from '../state-province-dropdown/state-province-dropdown';
import { CountryModel } from '../../core/models/data/country.model';
import { AddressFormValue } from '../../core/models/ui/address-form-value.model';
import { CountryAlpha2Codes } from '../../core/enums/data.enums';
import { FormMask, FormValidatorMessages } from '../../core/enums/forms.enums';
import { FormValidationRegEx } from '../../core/constants/constants';

@Component({
	selector: 'ms-address-form',
	imports: [InputLabel, ReactiveFormsModule, CountryDropdown, StateProvinceDropdown, NgxMaskDirective],
	providers: [
		provideNgxMask(),
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => AddressForm),
			multi: true
		},
		{
			provide: NG_VALIDATORS,
			useExisting: forwardRef(() => AddressForm),
			multi: true
		}
	],
	templateUrl: './address.form.html',
	styleUrl: './address.form.scss'
})
export class AddressForm implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor, Validator {
	//region Properties & Signals
	public required = input<boolean>(true);

	// Set by hosts that pre-select a country (e.g. the Property form) so the label can
	// explain where the default came from. No icon renders when it is left unset.
	public countryTooltip = input<string | undefined>(undefined);

	public FormValidatorMessages = FormValidatorMessages;
	public selectedCountry = signal<CountryModel | undefined>(undefined);
	public zipPostalMaskPattern = signal(FormMask.ZipPostal_US_Pattern);
	public zipPostalInvalidPatternValidatorMessage = signal(FormValidatorMessages.ZipPostal_US_Pattern);

	// The only mask separator the stored value drops. The US ZIP+4 hyphen is formatting, but the
	// Canadian space is part of the postal code and has to reach what gets verified and saved
	// (DEV-2783). International codes carry no mask, so nothing is stripped from them at all.
	public readonly zipPostalSeparatorsToDrop: string[] = ['-'];

	public addressForm: FormGroup;

	private changeDetectorRef = inject(ChangeDetectorRef);

	private onChange = (_value: any) => { };
	private onTouchedCallback = () => { };
	private formSubscription: Subscription | null = null;
	private initialized = false;

	// writeValue() and reset() push a country into the dropdown, whose model signal emits a
	// change exactly as a user pick does. The pick side effects must not run for those
	// writes: clearing state/zip would wipe the values being written, and the resulting
	// valueChanges would report a form-level edit the user never made — leaving the host's
	// control dirty and non-empty after a reset.
	private isWritingValue = false;

	constructor(private fb: FormBuilder) {
		this.addressForm = this.fb.group({
			country: [undefined],
			primaryAddress: [''],
			secondaryAddress: [''],
			city: [''],
			stateProvince: [''],
			zipPostal: ['']
		});

		effect(() => {
			const isRequired = this.required();
			this.updateValidators(isRequired);
		});
	}

	public ngOnInit(): void {
		this.formSubscription = this.addressForm.valueChanges.subscribe(() => {
			if (this.initialized) {
				this.onChange(this.addressForm.value as AddressFormValue);
			}
		});

		this.updateValidators(this.required());
	}

	public ngAfterViewInit(): void {
		this.initialized = true;
	}

	//region ControlValueAccessor

	public writeValue(value: AddressFormValue | null | undefined): void {
		this.isWritingValue = true;

		try {
			if (!value) {
				this.addressForm.reset({
					country: undefined,
					primaryAddress: '',
					secondaryAddress: '',
					city: '',
					stateProvince: '',
					zipPostal: ''
				}, { emitEvent: false });
				this.selectedCountry.set(undefined);
				this.applyCountryZipRules();
			} else {
				// The zip mask has to reach the input before the zip does. ngx-mask draws a written
				// value with whatever mask it holds at that moment, and a later change to the empty
				// international mask does not redraw it, so an international code would stay drawn
				// through the previous country's mask — the US default strips every letter (DEV-2784).
				this.selectedCountry.set(value.country);
				this.applyCountryZipRules();
				if (this.initialized) {
					this.changeDetectorRef.detectChanges();
				}

				this.addressForm.patchValue({
					country: value.country,
					primaryAddress: value.primaryAddress || '',
					secondaryAddress: value.secondaryAddress || '',
					city: value.city || '',
					stateProvince: value.stateProvince,
					zipPostal: value.zipPostal || ''
				}, { emitEvent: false });
			}
		} finally {
			this.isWritingValue = false;
		}
	}

	public registerOnChange(fn: (value: any) => void): void {
		this.onChange = fn;
	}

	public registerOnTouched(fn: () => void): void {
		this.onTouchedCallback = fn;
	}

	//endregion ControlValueAccessor

	//region Validator

	// Called automatically by Angular via the NG_VALIDATORS provider registration
	public validate(_control: AbstractControl): ValidationErrors | null {
		if (!this.required()) {
			return null;
		}
		if (this.addressForm.invalid) {
			return { addressInvalid: true };
		}
		return null;
	}

	//endregion Validator

	//region Public Methods

	public selectedCountryChange(): void {
		if (this.isWritingValue) {
			return;
		}

		const stateProvince = this.addressForm?.get('stateProvince');
		stateProvince?.markAsUntouched();
		stateProvince?.setValue(undefined);

		const zipPostal = this.addressForm?.get('zipPostal');
		zipPostal?.markAsUntouched();
		zipPostal?.setValue('');

		this.applyCountryZipRules();
	}

	public isFieldRequired(field: string): boolean {
		const control = this.addressForm.get(field);
		return control?.hasValidator(Validators.required) || false;
	}

	public onBlur(): void {
		this.onTouchedCallback();
	}

	public markAllAsTouched(): void {
		this.addressForm.markAllAsTouched();
	}

	public reset(): void {
		this.isWritingValue = true;

		try {
			this.addressForm.reset({
				country: undefined,
				primaryAddress: '',
				secondaryAddress: '',
				city: '',
				stateProvince: '',
				zipPostal: ''
			}, { emitEvent: false });
			this.selectedCountry.set(undefined);
			this.applyCountryZipRules();
		} finally {
			this.isWritingValue = false;
		}
	}

	//endregion Public Methods

	//region Private Methods

	// The zip mask, its error message and its pattern validator are all derived from the
	// selected country, so every path that changes the country — a user pick, writeValue()
	// or reset() — has to re-derive all three together. A country of undefined lands on the
	// default (US) rules rather than leaving the previous country's behind.
	private applyCountryZipRules(): void {
		this.updateZipPatternByCountry(this.selectedCountry()?.alpha2Code);
		this.updateValidators(this.required());
	}

	private updateValidators(isRequired: boolean): void {
		const country = this.addressForm?.get('country');
		const primaryAddress = this.addressForm?.get('primaryAddress');
		const secondaryAddress = this.addressForm?.get('secondaryAddress');
		const city = this.addressForm?.get('city');
		const stateProvince = this.addressForm?.get('stateProvince');
		const zipPostal = this.addressForm?.get('zipPostal');

		if (isRequired) {
			country?.setValidators([Validators.required]);
			primaryAddress?.setValidators([Validators.required]);
			secondaryAddress?.setValidators(null);
			city?.setValidators([Validators.required]);
			stateProvince?.setValidators([Validators.required]);
			zipPostal?.setValidators([Validators.required, Validators.pattern(this.getZipPostalRegExPatternByCountry())]);
		} else {
			country?.setValidators(null);
			primaryAddress?.setValidators(null);
			secondaryAddress?.setValidators(null);
			city?.setValidators(null);
			stateProvince?.setValidators(null);
			zipPostal?.setValidators(null);
		}

		country?.updateValueAndValidity({ emitEvent: false });
		primaryAddress?.updateValueAndValidity({ emitEvent: false });
		city?.updateValueAndValidity({ emitEvent: false });
		stateProvince?.updateValueAndValidity({ emitEvent: false });
		zipPostal?.updateValueAndValidity({ emitEvent: false });
	}

	// Only the US and Canada have a format worth enforcing here. The seven international countries
	// each have their own, so the field takes a shape check and the verification service rules on
	// whether the code actually exists. No country selected lands on the US rules (DEV-716).
	private getZipPostalRegExPatternByCountry(): RegExp {
		switch (this.selectedCountry()?.alpha2Code?.toUpperCase()) {
			case CountryAlpha2Codes.Canada:
				return FormValidationRegEx.ZIP_POSTAL_CA_PATTERN();
			case CountryAlpha2Codes.UnitedStatesOfAmerica:
			case undefined:
				return FormValidationRegEx.ZIP_POSTAL_US_PATTERN();
			default:
				return FormValidationRegEx.ZIP_POSTAL_INTERNATIONAL_PATTERN();
		}
	}

	private updateZipPatternByCountry(alpha2Code?: string): void {
		switch (alpha2Code?.toUpperCase()) {
			case CountryAlpha2Codes.Canada:
				this.zipPostalMaskPattern.set(FormMask.ZipPostal_CA_Pattern);
				this.zipPostalInvalidPatternValidatorMessage.set(FormValidatorMessages.ZipPostal_CA_Pattern);
				break;
			case CountryAlpha2Codes.UnitedStatesOfAmerica:
			case undefined:
				this.zipPostalMaskPattern.set(FormMask.ZipPostal_US_Pattern);
				this.zipPostalInvalidPatternValidatorMessage.set(FormValidatorMessages.ZipPostal_US_Pattern);
				break;
			default:
				this.zipPostalMaskPattern.set(FormMask.ZipPostal_International_Pattern);
				this.zipPostalInvalidPatternValidatorMessage.set(FormValidatorMessages.ZipPostal_International_Pattern);
				break;
		}
	}

	//endregion Private Methods

	public ngOnDestroy(): void {
		this.formSubscription?.unsubscribe();
	}

}
